/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import React, { useEffect, useState, useRef } from 'react'
import type { TreeProps, ArtifactNode } from './types'

const Tree: React.FC<TreeProps> = ({ artifactData, onChange, selectedArticafts }) => {
  const [isExpanded, setIsExpanded] = useState(false)
  const previousExpandedRef = useRef<boolean | undefined>(undefined)

  useEffect(() => {
    if (artifactData) {
      // Expand node when artifactData.expanded changes from false/undefined to true
      // This handles external expansion (e.g., from dropdown selections)
      if (artifactData.expanded && previousExpandedRef.current !== artifactData.expanded) {
        setIsExpanded(true)
      }
      previousExpandedRef.current = artifactData.expanded
    }
  }, [artifactData?.expanded, artifactData?.containsContaining])

  const handleCheckboxSelection = (id: number | string, isChecked: boolean) => {
    onChange(id, isChecked)
  }

  const isChecked = () => {
    if (!artifactData?.isArtifact) {
      return artifactData?.selected ?? false
    }
    return artifactData?.id != undefined ? selectedArticafts.includes(artifactData.id) : false
  }

  const getSelectionCounts = (node: ArtifactNode): { selected: number; total: number } => {
    if (!node?.children || node.children?.length === 0) {
      return { selected: node.selected ? 1 : 0, total: 1 }
    }
    return node.children.reduce(
      (acc, child) => {
        const counts = getSelectionCounts(child)
        return {
          selected: acc.selected + counts.selected,
          total: acc.total + counts.total
        }
      },
      { selected: node.selected ? 1 : 0, total: 1 }
    )
  }

  const getClassName = () => {
    if (!artifactData?.isArtifact) {
      const { selected, total } = getSelectionCounts(artifactData)
      if (selected > 0 && selected < total) {
        return 'checked-indicator'
      }
    }
    return ''
  }

  const handleExpandAction = (artifactData: ArtifactNode) => {
    artifactData.expanded = !isExpanded
    setIsExpanded((value) => !value)
  }

  const artifactItem = (
    <div
      className="d-flex"
      onClick={() =>
        handleCheckboxSelection(
          artifactData?.id ? artifactData.id : (artifactData?.identifier as any),
          !artifactData?.selected
        )
      }
      key={artifactData?.id}
    >
      <div className={`line-height-for-packages-selector checkbox-form-group ${isExpanded ? 'expanded' : ''}`}>
        <input
          type="checkbox"
          className={getClassName()}
          id={'artifact-selector-' + artifactData?.id}
          name={'artifact-selector-' + artifactData?.id}
          checked={isChecked()}
        />
        <label
          htmlFor={'artifact-selector-' + artifactData?.id}
          style={{ fontWeight: 'normal' }}
          className="pointer-events-none"
        >
          <div className="chkbc" />
        </label>
      </div>
      <span className="artifact-name">{artifactData?.name}</span>
    </div>
  )

  return (
    <div className="level-spacer">
      {artifactData?.hasChildren ? (
        <div className="artifact-row">
          <span className="align-content-center" onClick={() => handleExpandAction(artifactData)}>
            <i className={`fa ${isExpanded ? 'fa-caret-down' : 'fa-caret-right'} fa-lg`} aria-hidden="true"></i>
          </span>
          {artifactItem}
        </div>
      ) : (
        <div className="artifact-row">{artifactItem}</div>
      )}

      {isExpanded && (
        <div>
          {artifactData?.children?.map((child) => (
            <Tree key={child?.id} artifactData={child} onChange={onChange} selectedArticafts={selectedArticafts} />
          ))}
        </div>
      )}
    </div>
  )
}

export default Tree
